import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import type { OwnerPet } from '@/api/patients/patientsApi.types';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/common/Icon';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { PATIENT_CASE_STATUS } from '@/constants/patients';
import { formatPatientAge } from '@/utils/patientAge';

interface PetCardProps {
  pet: OwnerPet;
}

export function PetCard({ pet }: PetCardProps) {
  const { t } = useTranslation('common');
  const ageLabel = formatPatientAge(pet.dateOfBirth, t);
  const meta = [pet.species, pet.breed]
    .filter(function keep(part) {
      return part !== null && part.trim().length > 0;
    })
    .join(' · ');
  return (
    <Link
      to={generatePath(APPLICATION_ROUTES.clientPortalPet, { patientId: pet.id })}
      className="flex items-center gap-3 p-3 border border-zooborder rounded-xl hover:border-zooprimary hover:bg-zoovpalelavender hover:bg-opacity-30 transition-colors"
    >
      <span className="w-12 h-12 rounded-xl bg-zoovpalelavender flex items-center justify-center overflow-hidden shrink-0">
        {pet.photoUrl === null ? (
          <Icon name="PawPrint" className="text-zooprimary" />
        ) : (
          <img
            src={pet.photoUrl}
            alt={pet.name}
            width={48}
            height={48}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        )}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-bold text-zooindigo truncate">{pet.name}</span>
          {pet.status === PATIENT_CASE_STATUS.active ? null : (
            <Badge tone="muted" size="sm">
              {t('patients:status.' + pet.status)}
            </Badge>
          )}
        </div>
        <div className="text-xs text-zoomuted truncate">
          {meta}
          {ageLabel === null ? '' : ' · ' + ageLabel}
        </div>
      </div>
    </Link>
  );
}
